import { useRef, useState, useCallback, useEffect } from "react";
import { createWorker } from "tesseract.js";

/**
 * useSmartCapture – On-device auto-quality-check + auto-zoom for tool labels.
 *
 * Workflow:
 *   camera preview → continuous autofocus
 *   → on-device checks (edges, sharpness, brightness)
 *   → Tesseract.js verifies readable text in Web Worker
 *   → if sharp but Tesseract can't read → auto-increment digital zoom
 *   → GREEN when Tesseract confirms readable text
 *   → user presses capture → 3 exposures → score → best image → backend
 *
 * Overlay colours & hints:
 *   RED    = no label / bad exposure         → "Align label"
 *   RED    = blurry (too close)              → "Move back slightly"
 *   YELLOW = on-device OK, Tesseract checking → "Checking label..." / "Adjusting zoom..."
 *   GREEN  = Tesseract confirmed readable    → "Ready - press capture"
 */

// ─── Fast on-device image analysis ───────────────────────────────────────

function toGray(imageData) {
  const { data, width, height } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    const idx = i * 4;
    gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
  }
  return { gray, width, height };
}

function calcSharpness(grayObj) {
  const { gray, width, height } = grayObj;
  let sum = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const lap =
        gray[(y - 1) * width + x] +
        gray[(y + 1) * width + x] +
        gray[y * width + (x - 1)] +
        gray[y * width + (x + 1)] -
        4 * gray[y * width + x];
      sum += lap * lap;
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

function calcBrightness(grayObj) {
  const { gray } = grayObj;
  let sum = 0;
  for (let i = 0; i < gray.length; i++) sum += gray[i];
  return gray.length > 0 ? sum / gray.length : 0;
}

function detectEdges(grayObj) {
  const { gray, width, height } = grayObj;
  const x0 = Math.floor(width * 0.2);
  const x1 = Math.floor(width * 0.8);
  const y0 = Math.floor(height * 0.2);
  const y1 = Math.floor(height * 0.8);
  let edgeCount = 0;
  let totalPixels = 0;
  for (let y = y0 + 1; y < y1 - 1; y++) {
    for (let x = x0 + 1; x < x1 - 1; x++) {
      const gx = -gray[y * width + (x - 1)] + gray[y * width + (x + 1)];
      const gy = -gray[(y - 1) * width + x] + gray[(y + 1) * width + x];
      if (Math.abs(gx) + Math.abs(gy) > 30) edgeCount++;
      totalPixels++;
    }
  }
  return totalPixels > 0 ? edgeCount / totalPixels : 0;
}

function scoreFrame(imageData) {
  const grayObj = toGray(imageData);
  const sharpness = calcSharpness(grayObj);
  const brightness = calcBrightness(grayObj);
  const sharpNorm = Math.min(sharpness / 300, 1);
  const brightDist = Math.abs(brightness - 125) / 125;
  return sharpNorm * 0.6 + (1 - brightDist) * 0.4;
}

// ─── Thresholds ──────────────────────────────────────────────────────────

const SHARPNESS_THRESHOLD = 80;
const BRIGHTNESS_MIN = 50;
const BRIGHTNESS_MAX = 200;
const EDGE_DENSITY_MIN = 0.08;
const TESSERACT_MIN_CONFIDENCE = 35;
const TESSERACT_MIN_CHARS = 2;
const MAX_CAPTURE_WIDTH = 1600;
const EXPOSURE_OFFSETS = [-1.0, 0, 0.7];

// Zoom — fixed level to help iPhones focus at comfortable distance
const FIXED_ZOOM = 1.5;

// ─── Helpers: draw a zoomed (centre-cropped) region from video ───────────

/**
 * Draw the centre-cropped portion of the video onto a canvas at the given
 * zoom level. Returns the canvas context (caller can getImageData / toBlob).
 */
function drawZoomed(video, canvas, targetW, targetH, zoom) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  // Source rect: centre crop based on zoom
  const sw = vw / zoom;
  const sh = vh / zoom;
  const sx = (vw - sw) / 2;
  const sy = (vh - sh) / 2;

  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);
  return ctx;
}

// ─── Canvas unsharp mask — sharpens text edges for better OCR ────────────

function sharpenCanvas(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const w = canvas.width;
  const h = canvas.height;
  const src = ctx.getImageData(0, 0, w, h);
  const dst = ctx.createImageData(w, h);
  const sd = src.data;
  const dd = dst.data;

  // 3x3 sharpen kernel: [0,-1,0 / -1,5,-1 / 0,-1,0]
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4;
      for (let c = 0; c < 3; c++) {
        const val =
          5 * sd[i + c] -
          sd[((y - 1) * w + x) * 4 + c] -
          sd[((y + 1) * w + x) * 4 + c] -
          sd[(y * w + (x - 1)) * 4 + c] -
          sd[(y * w + (x + 1)) * 4 + c];
        dd[i + c] = Math.max(0, Math.min(255, val));
      }
      dd[i + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useSmartCapture({ webcamRef, active, onCapture }) {
  const canvasRef = useRef(document.createElement("canvas"));
  const rafRef = useRef(null);
  const capturedRef = useRef(false);
  const workerRef = useRef(null);
  const ocrBusyRef = useRef(false);
  const ocrConfirmedRef = useRef(false);
  const isBlurryRef = useRef(false);

  // "red" | "yellow" | "green"
  const [quality, setQuality] = useState("red");
  const [capturing, setCapturing] = useState(false);
  const zoom = FIXED_ZOOM;
  // "align" | "move-back" | "checking" | "ready"
  const [hint, setHint] = useState("align");

  // ── Tesseract worker lifecycle ────────────────────────────────────────
  useEffect(() => {
    if (!active) {
      if (workerRef.current) {
        workerRef.current.terminate().catch(() => {});
        workerRef.current = null;
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const worker = await createWorker("eng", 1, {
          logger: () => {},
        });
        if (cancelled) {
          await worker.terminate();
          return;
        }
        workerRef.current = worker;
      } catch (e) {
        console.warn("Tesseract worker init failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      if (workerRef.current) {
        workerRef.current.terminate().catch(() => {});
        workerRef.current = null;
      }
    };
  }, [active]);

  // ── Grab a small analysis frame (zoomed) ──────────────────────────────
  const grabAnalysisFrame = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;
    const canvas = canvasRef.current;
    const h = Math.round(video.videoHeight * (320 / video.videoWidth));
    const ctx = drawZoomed(video, canvas, 320, h, FIXED_ZOOM);
    return ctx.getImageData(0, 0, 320, h);
  }, [webcamRef]);

  // ── Get a zoomed data URL for Tesseract ───────────────────────────────
  const getOcrFrameDataUrl = useCallback(() => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;
    const canvas = canvasRef.current;
    const h = Math.round(video.videoHeight * (640 / video.videoWidth));
    drawZoomed(video, canvas, 640, h, FIXED_ZOOM);
    return canvas.toDataURL("image/jpeg", 0.8);
  }, [webcamRef]);

  // ── Run Tesseract OCR check (async) ───────────────────────────────────
  const runOcrCheck = useCallback(async () => {
    if (ocrBusyRef.current || !workerRef.current) return;
    ocrBusyRef.current = true;

    try {
      const dataUrl = getOcrFrameDataUrl();
      if (!dataUrl) {
        ocrBusyRef.current = false;
        return;
      }

      const { data } = await workerRef.current.recognize(dataUrl);
      const text = (data.text || "").trim();
      const confidence = data.confidence || 0;

      if (text.length >= TESSERACT_MIN_CHARS && confidence >= TESSERACT_MIN_CONFIDENCE) {
        ocrConfirmedRef.current = true;
      } else {
        ocrConfirmedRef.current = false;
      }
    } catch {
      ocrConfirmedRef.current = false;
    } finally {
      ocrBusyRef.current = false;
    }
  }, [getOcrFrameDataUrl]);

  // ── Capture full-res zoomed frame (max 1600px) ────────────────────────
  const captureFullFrame = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;
    const canvas = canvasRef.current;

    // Target size: zoom-cropped, max 1600px wide
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > MAX_CAPTURE_WIDTH) {
      const ratio = MAX_CAPTURE_WIDTH / w;
      w = MAX_CAPTURE_WIDTH;
      h = Math.round(h * ratio);
    }

    drawZoomed(video, canvas, w, h, FIXED_ZOOM);

    // Apply unsharp mask to enhance text edges for OCR
    sharpenCanvas(canvas);

    // Score on a small copy
    const sc = document.createElement("canvas");
    const sw = 320;
    const sh = Math.round(h * (320 / w));
    sc.width = sw;
    sc.height = sh;
    sc.getContext("2d").drawImage(canvas, 0, 0, sw, sh);
    const imageData = sc
      .getContext("2d", { willReadFrequently: true })
      .getImageData(0, 0, sw, sh);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
    });
    return { blob, imageData };
  }, [webcamRef]);

  // ── Exposure control ──────────────────────────────────────────────────
  const setExposure = useCallback(async (ev) => {
    const video = webcamRef.current?.video;
    if (!video) return false;
    const stream = video.srcObject;
    if (!stream) return false;
    const track = stream.getVideoTracks()[0];
    if (!track) return false;
    try {
      const caps = track.getCapabilities();
      if (caps.exposureMode && caps.exposureCompensation) {
        const { min, max } = caps.exposureCompensation;
        await track.applyConstraints({
          advanced: [
            {
              exposureMode: "manual",
              exposureCompensation: Math.max(min, Math.min(max, ev)),
            },
          ],
        });
        return true;
      }
    } catch {
      /* not supported */
    }
    return false;
  }, [webcamRef]);

  const resetExposure = useCallback(async () => {
    const video = webcamRef.current?.video;
    if (!video) return;
    const stream = video.srcObject;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({
        advanced: [{ exposureMode: "continuous" }],
      });
    } catch {
      /* ignore */
    }
  }, [webcamRef]);

  // ── Multi-exposure capture: 3 frames → score → pick best ─────────────
  const doCapture = useCallback(async () => {
    if (capturedRef.current) return;
    capturedRef.current = true;
    setCapturing(true);

    const frames = [];
    const hwSupported = await setExposure(0);

    if (hwSupported) {
      for (const ev of EXPOSURE_OFFSETS) {
        await setExposure(ev);
        await new Promise((r) => setTimeout(r, 250));
        const result = await captureFullFrame();
        if (result)
          frames.push({ score: scoreFrame(result.imageData), blob: result.blob });
      }
      await resetExposure();
    } else {
      for (let i = 0; i < 3; i++) {
        const result = await captureFullFrame();
        if (result)
          frames.push({ score: scoreFrame(result.imageData), blob: result.blob });
        if (i < 2) await new Promise((r) => setTimeout(r, 200));
      }
    }

    if (frames.length === 0) {
      const result = await captureFullFrame();
      if (result) frames.push({ score: 0, blob: result.blob });
    }

    frames.sort((a, b) => b.score - a.score);
    if (frames[0]) onCapture(frames[0].blob);

    setCapturing(false);
  }, [captureFullFrame, setExposure, resetExposure, onCapture]);

  // ── Main analysis loop ────────────────────────────────────────────────
  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      capturedRef.current = false;
      ocrConfirmedRef.current = false;
      ocrBusyRef.current = false;
      isBlurryRef.current = false;
      setQuality("red");
      setHint("align");
      return;
    }

    let frameSkip = 0;

    const analyse = () => {
      if (capturedRef.current) return;

      frameSkip++;
      if (frameSkip % 2 !== 0) {
        rafRef.current = requestAnimationFrame(analyse);
        return;
      }

      const imgData = grabAnalysisFrame();
      if (!imgData) {
        rafRef.current = requestAnimationFrame(analyse);
        return;
      }

      const grayObj = toGray(imgData);
      const edgeDensity = detectEdges(grayObj);
      const sharpness = calcSharpness(grayObj);
      const brightness = calcBrightness(grayObj);

      const hasEdges = edgeDensity >= EDGE_DENSITY_MIN;
      const isSharp = sharpness >= SHARPNESS_THRESHOLD;
      const exposureOk = brightness >= BRIGHTNESS_MIN && brightness <= BRIGHTNESS_MAX;

      isBlurryRef.current = hasEdges && !isSharp;

      if (!hasEdges || !exposureOk) {
        setQuality("red");
        setHint("align");
        ocrConfirmedRef.current = false;
      } else if (!isSharp) {
        // Blurry — likely too close
        setQuality("red");
        setHint("move-back");
        ocrConfirmedRef.current = false;
      } else {
        // On-device checks pass
        if (ocrConfirmedRef.current) {
          setQuality("green");
          setHint("ready");
        } else {
          setQuality("yellow");
          setHint("checking");
          runOcrCheck();
        }
      }

      rafRef.current = requestAnimationFrame(analyse);
    };

    const startDelay = setTimeout(() => {
      rafRef.current = requestAnimationFrame(analyse);
    }, 600);

    return () => {
      clearTimeout(startDelay);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, grabAnalysisFrame, runOcrCheck]);

  const manualCapture = useCallback(() => {
    doCapture();
  }, [doCapture]);

  return { quality, capturing, manualCapture, zoom, hint };
}
